import React, { useState, useRef, useEffect } from 'react';
import './WorkspaceTreeNav.css';

const WorkspaceTreeNav = ({
  userId,
  onFilterByTask,
  toggleStackExpansion
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedWorkspace, setExpandedWorkspace] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Store projects, jobs, and tasks data
  const [projectsData, setProjectsData] = useState({});
  const [jobsData, setJobsData] = useState({});
  const [tasksData, setTasksData] = useState({});
  const [loadingProjects, setLoadingProjects] = useState({});
  const [loadingJobs, setLoadingJobs] = useState({});
  const [loadingTasks, setLoadingTasks] = useState({});

   const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

   // Fetch workspaces when dropdown is opened
  const fetchWorkspaces = async () => {
    setLoading(true);
    setError(null);
    try {

      const response = await fetch(`${apiUrl}/Workspace/GetWorkspacesByUserId/${userId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "workspaces retrieved successfully" && data.workspaces) {
        // Filter only active workspaces (status === 1)
        const activeWorkspaces = data.workspaces.filter(workspace => workspace.status === 1);
        setWorkspaces(activeWorkspaces);
      } else {
        setWorkspaces([]);
        setError("No workspaces found");
      }
    } catch (err) {
      console.error("Error fetching workspaces:", err);
      setError("Failed to load workspaces");
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects for a specific workspace
  const fetchProjects = async (workspaceId) => {
    setLoadingProjects(prev => ({ ...prev, [workspaceId]: true }));
    try {
     
      const response = await fetch(`${apiUrl}/Filters/GetFilteredProject/${userId}?WorkspaceId=${workspaceId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "filtered project retrieved successfully" && data.jobs) {
        // Transform the jobs array into projects array
        const projects = data.jobs.map(job => ({
          id: job.projectId,
          name: job.projectName,
          workspaceId: workspaceId,
          status: 1
        }));
        
        // Filter only active projects
        const activeProjects = projects.filter(project => project.status === 1);
        
        setProjectsData(prev => ({
          ...prev,
          [workspaceId]: activeProjects
        }));
      } else {
        setProjectsData(prev => ({
          ...prev,
          [workspaceId]: []
        }));
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjectsData(prev => ({
        ...prev,
        [workspaceId]: []
      }));
    } finally {
      setLoadingProjects(prev => ({ ...prev, [workspaceId]: false }));
    }
  };

  // Fetch jobs for a specific project
  const fetchJobs = async (projectId) => {
    setLoadingJobs(prev => ({ ...prev, [projectId]: true }));
    try {
     
      const response = await fetch(`${apiUrl}/Filters/GetFilteredJob/${userId}?ProjectId=${projectId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "filtered job retrieved successfully" && data.jobs) {
        // Transform the jobs array
        const jobs = data.jobs.map(job => ({
          id: job.jobId,
          name: job.jobName,
          projectId: projectId,
          status: 1
        }));
        
        // Filter only active jobs
        const activeJobs = jobs.filter(job => job.status === 1);
        
        setJobsData(prev => ({
          ...prev,
          [projectId]: activeJobs
        }));
      } else {
        setJobsData(prev => ({
          ...prev,
          [projectId]: []
        }));
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setJobsData(prev => ({
        ...prev,
        [projectId]: []
      }));
    } finally {
      setLoadingJobs(prev => ({ ...prev, [projectId]: false }));
    }
  };



  // Fetch tasks for a specific job
  const fetchTasks = async (jobId) => {
    setLoadingTasks(prev => ({ ...prev, [jobId]: true }));
    try {
    
      const response = await fetch(`${apiUrl}/Filters/GetFilteredTask/${userId}?JobId=${jobId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "Filtered tasks retrieved successfully" && data.tasks) {
        // Transform the tasks array
        const tasks = data.tasks.map(task => ({
          id: task.taskId,
          name: task.taskTitle,
          jobId: jobId
        }));
        
        setTasksData(prev => ({
          ...prev,
          [jobId]: tasks
        }));
      } else {
        setTasksData(prev => ({
          ...prev,
          [jobId]: []
        }));
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setTasksData(prev => ({
        ...prev,
        [jobId]: []
      }));
    } finally {
      setLoadingTasks(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Handle workspace click - fetch projects
  const handleWorkspaceClick = async (workspaceId) => {
    if (expandedWorkspace === workspaceId) {
      // Collapse if already expanded
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      // Expand and fetch projects if not already loaded
      setExpandedWorkspace(workspaceId);
      setExpandedProject(null);
      setExpandedJob(null);
      
      // Fetch projects if not already loaded
      if (!projectsData[workspaceId]) {
        await fetchProjects(workspaceId);
      }
    }
  };

  // Handle project click - fetch jobs
  const handleProjectClick = async (projectId) => {
    if (expandedProject === projectId) {
      // Collapse if already expanded
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      // Expand and fetch jobs if not already loaded
      setExpandedProject(projectId);
      setExpandedJob(null);
      
      // Fetch jobs if not already loaded
      if (!jobsData[projectId]) {
        await fetchJobs(projectId);
      }
    }
  };

  // Handle job click - fetch tasks
  const handleJobClick = async (jobId) => {
    if (expandedJob === jobId) {
      // Collapse if already expanded
      setExpandedJob(null);
    } else {
      // Expand and fetch tasks if not already loaded
      setExpandedJob(jobId);
      
      // Fetch tasks if not already loaded
      if (!tasksData[jobId]) {
        await fetchTasks(jobId);
      }
    }
  };

// In WorkspaceTreeNav.js
const handleTaskClick = async (task, job) => {
  console.log('Selected task:', task, 'for job:', job);
  
  // Find the complete job object with all necessary data
  const completeJob = {
    jobId: job.id,
    jobName: job.name,
    workspaceId: job.workspaceId,
    workspaceName: job.workspaceName,
    projectId: job.projectId,
    projectName: job.projectName,
    // Add any other job properties needed
  };
  
  // Call the callback with task and job info
  if (onFilterByTask) {
    onFilterByTask(task.id, task.name, completeJob);
  }
  
  // Close the dropdown
  setIsOpen(false);
  
  // Reset expanded states
  setExpandedWorkspace(null);
  setExpandedProject(null);
  setExpandedJob(null);
};

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset expanded states when closing
        setExpandedWorkspace(null);
        setExpandedProject(null);
        setExpandedJob(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset expanded states when opening
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
      // Fetch workspaces when opening dropdown
      fetchWorkspaces();
    }
  };

  return (
    <div className="workspace-tree-nav" ref={dropdownRef}>
      <button
        className="workspace-toggle-btn"
        onClick={toggleDropdown}
        title="Workspace Navigation"
      >
        <i className="fas fa-layer-group" />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} toggle-icon`} />
      </button>

      {isOpen && (
        <div className="workspace-dropdown slide-in-left">
          <div className="workspace-header">
            <i className="fas fa-sitemap" />
            <span>Workspace Navigation</span>
          </div>
          
          <div className="workspace-tree-content">
            {loading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Loading workspaces...</span>
              </div>
            ) : error ? (
              <div className="error-state">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
                <button onClick={fetchWorkspaces} className="retry-btn">
                  <i className="fas fa-redo" />
                  Retry
                </button>
              </div>
            ) : workspaces.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building" />
                <span>No active workspaces found</span>
              </div>
            ) : (
              workspaces.map((workspace) => {
                const workspaceProjects = projectsData[workspace.id] || [];
                const isLoadingProjects = loadingProjects[workspace.id];
                
                return (
                  <div key={workspace.id} className="workspace-node">
                    <div
                      className="workspace-item"
                      onClick={() => handleWorkspaceClick(workspace.id)}
                    >
                      <i className={`fas fa-chevron-${expandedWorkspace === workspace.id ? 'down' : 'right'} expand-icon`} />
                      <i className="fas fa-building workspace-icon" />
                      <div className="workspace-info">
                        <div className="workspace-name">{workspace.name}</div>
                      </div>
                      <span className="project-count">
                        ({workspaceProjects.length})
                      </span>
                    </div>
                    
                    {expandedWorkspace === workspace.id && (
                      <div className="projects-container">
                        {isLoadingProjects ? (
                          <div className="loading-projects">
                            <i className="fas fa-spinner fa-spin" />
                            <span>Loading projects...</span>
                          </div>
                        ) : workspaceProjects.length === 0 ? (
                          <div className="no-projects-message">
                            <i className="fas fa-project-diagram" />
                            <span>No active projects available</span>
                          </div>
                        ) : (
                          workspaceProjects.map((project) => {
                            const projectJobs = jobsData[project.id] || [];
                            const isLoadingJobs = loadingJobs[project.id];
                            
                            return (
                              <div key={project.id} className="project-node">
                                <div
                                  className="project-item"
                                  onClick={() => handleProjectClick(project.id)}
                                >
                                  <i className={`fas fa-chevron-${expandedProject === project.id ? 'down' : 'right'} expand-icon`} />
                                  <i className="fas fa-project-diagram project-icon" />
                                  <span className="project-name">{project.name}</span>
                                  <span className="job-count">
                                    ({projectJobs.length})
                                  </span>
                                </div>
                                
                                {expandedProject === project.id && (
                                  <div className="jobs-container">
                                    {isLoadingJobs ? (
                                      <div className="loading-jobs">
                                        <i className="fas fa-spinner fa-spin" />
                                        <span>Loading jobs...</span>
                                      </div>
                                    ) : projectJobs.length === 0 ? (
                                      <div className="no-jobs-message">
                                        <i className="fas fa-tasks" />
                                        <span>No active jobs available</span>
                                      </div>
                                    ) : (
                                      projectJobs.map((job) => {
                                        const jobTasks = tasksData[job.id] || [];
                                        const isLoadingTasks = loadingTasks[job.id];
                                        
                                        return (
                                          <div key={job.id} className="job-node">
                                            <div
                                              className="job-item"
                                              onClick={() => handleJobClick(job.id)}
                                            >
                                              <i className={`fas fa-chevron-${expandedJob === job.id ? 'down' : 'right'} expand-icon`} />
                                              <i className="fas fa-tasks job-icon" />
                                              <span className="job-name">{job.name}</span>
                                              <span className="task-count">
                                                ({jobTasks.length})
                                              </span>
                                            </div>
                                            
                                            {expandedJob === job.id && (
                                              <div className="tasks-container">
                                                {isLoadingTasks ? (
                                                  <div className="loading-tasks">
                                                    <i className="fas fa-spinner fa-spin" />
                                                    <span>Loading tasks...</span>
                                                  </div>
                                                ) : jobTasks.length === 0 ? (
                                                  <div className="no-tasks-message">
                                                    <i className="fas fa-check-square" />
                                                    <span>No tasks available</span>
                                                  </div>
                                                ) : (
                                                  jobTasks.map((task) => (
                                                    <div
                                                      key={task.id}
                                                      className="task-item"
                                                      onClick={() => handleTaskClick(task, job)}
                                                    >
                                                      <i className="fas fa-check-square task-icon" />
                                                      <span className="task-name">{task.name}</span>
                                                    </div>
                                                  ))
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceTreeNav;